/**
 * Stair traversal. Direction is a PLACE, not a keypress: the core carries an
 * up-stair and a down-stair side by side on every floor, and which one you're
 * standing on decides where you go.
 *
 * The rule that makes that work is the ARRIVAL: travelling up lands you on the
 * destination floor's DOWN tile (and vice versa). You arrive beside the way
 * back, and never on the tile that would immediately send you on again — so the
 * only thing stopping an infinite fall is geometry rather than a cooldown.
 *
 * Pure + headless (rule 4): main.ts owns the hold-to-travel timing and the
 * actual floor swap, this owns the decision.
 */

import { TileType } from '../tiles';
import type { Building } from './building';

/** Which way this tile sends you, or null if it isn't a stair. */
export function stairDirection(tile: TileType): 1 | -1 | null {
  if (tile === TileType.StairsUp) return 1;
  if (tile === TileType.StairsDown) return -1;
  return null;
}

/** Where travelling `dir` from `fromIdx` puts you, or null if there's no floor
 *  that way (the top floor's up-stair and the deepest basement's down-stair are
 *  both dead ends you can stand on harmlessly). */
export function stairArrival(
  building: Building,
  fromIdx: number,
  dir: 1 | -1,
): { floorIdx: number; at: { x: number; y: number } } | null {
  const floorIdx = fromIdx + dir;
  const target = building.floors[floorIdx];
  if (!target) return null;
  // Up → arrive on their DOWN tile; down → arrive on their UP tile.
  return { floorIdx, at: dir > 0 ? target.stairs.down : target.stairs.up };
}
